import { NO_PLACEMENT } from '../model/dto/video/video-constants';
import { StagedVideo } from '../model/type/video-file.types';
import {
  BatchAssignmentChoice,
  ProposedAssignment,
  StagingBlocker,
  StagingConsequence,
  StagingIssue,
  StagingIssueKind,
} from '../model/type/video-staging.types';
import { isPlacementPositionValid } from './video-placement-utils';

export const stagedFieldsId = (key: string): string => `staged-${key}`;

export const stagedFieldId = (key: string, kind: StagingIssueKind): string =>
  `${stagedFieldsId(key)}-${kind}`;

export const needsOrientation = (item: StagedVideo): boolean =>
  item.dimensions === null;

const issueKindsOf = (item: StagedVideo): StagingIssueKind[] => [
  ...(item.title.trim() ? [] : ['title' as const]),
  ...(needsOrientation(item) && !item.orientation
    ? ['orientation' as const]
    : []),
  ...(isPlacementPositionValid(item.placement) ? [] : ['position' as const]),
];

export const getStagingIssues = (staged: StagedVideo[]): StagingIssue[] =>
  staged.flatMap((item) =>
    issueKindsOf(item).map((kind) => ({
      key: item.key,
      fileName: item.file.name,
      kind,
      fieldId: stagedFieldId(item.key, kind),
    }))
  );

export const getStagingBlocker = (
  staged: StagedVideo[],
  canUpload: boolean
): StagingBlocker => {
  if (!canUpload) {
    return { kind: 'noPermission' };
  }

  if (!staged.length) {
    return { kind: 'noFiles' };
  }

  const issues = getStagingIssues(staged);

  if (issues.length) {
    return { kind: 'issues', issues };
  }

  return staged.some(({ dimensions }) => dimensions === 'reading')
    ? { kind: 'reading' }
    : { kind: 'none' };
};

export const isStagedPlaced = (item: StagedVideo): boolean =>
  item.placement.placement_key !== NO_PLACEMENT;

export const getStagingConsequence = (
  staged: StagedVideo[]
): StagingConsequence => {
  const placedCount = staged.filter(isStagedPlaced).length;

  return { placedCount, unplacedCount: staged.length - placedCount };
};

export const proposeBatchAssignment = (
  selected: StagedVideo[],
  { placementKey, startOrder, goals }: BatchAssignmentChoice
): ProposedAssignment[] =>
  selected.map(({ key, file, placement }, index) => ({
    key,
    fileName: file.name,
    values: {
      placement_key: placementKey ?? placement.placement_key,
      position:
        startOrder === null
          ? placement.position
          : String(Number(startOrder) + index),
      health_goals: goals ?? placement.health_goals,
    },
  }));
